import { SyntheticEvent } from 'react';

import { Box, Breadcrumbs, Link as MuiLink, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Container } from '~/src/components/Container';
import { PageHeader } from '~/src/components/PageHeader';

import { GlobalAlertBanner } from '../GlobalAlertBanner/GlobalAlertBanner';

const TAB_PATHS = [
  '/rate-cards/list',
  '/rate-cards/rate-diff',
  '/rate-cards/contract-mapping',
  '/rate-cards/approvals',
  '/rate-cards/activity-log',
  '/rate-cards/manual-upload',
] as const;

type TabPath = (typeof TAB_PATHS)[number];

export const RateCardManagerShell = () => {
  const { t } = useTranslation('rate-card-manager', { keyPrefix: 'shell' });
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const tabs = [
    { label: t('tabs.rateCards'), path: TAB_PATHS[0] },
    { label: t('tabs.rateDiff'), path: TAB_PATHS[1] },
    { label: t('tabs.contractMapping'), path: TAB_PATHS[2] },
    { label: t('tabs.approvals'), path: TAB_PATHS[3] },
    { label: t('tabs.activityLog'), path: TAB_PATHS[4] },
    { label: t('tabs.manualUpload'), path: TAB_PATHS[5] },
  ];

  const activeTab: TabPath =
    (TAB_PATHS.find((path) => pathname.startsWith(path)) as TabPath | undefined) ?? TAB_PATHS[0];

  const currentTabLabel = tabs.find(({ path }) => path === activeTab)?.label ?? tabs[0].label;

  const handleTabChange = (_: SyntheticEvent, newPath: TabPath) => {
    navigate(newPath);
  };

  return (
    <Container>
      <Breadcrumbs aria-label="breadcrumb">
        <MuiLink color="inherit" component={Link} to="/" underline="hover">
          {t('breadcrumbs.home')}
        </MuiLink>
        <MuiLink color="inherit" component={Link} to="/rate-cards/list" underline="hover">
          {t('breadcrumbs.rateCardManager')}
        </MuiLink>
        <Typography color="text.primary">{currentTabLabel}</Typography>
      </Breadcrumbs>

      <PageHeader title={t('title')} />

      <GlobalAlertBanner pendingCount={0} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          aria-label={t('tabs.ariaLabel')}
          indicatorColor="secondary"
          onChange={handleTabChange}
          scrollButtons="auto"
          textColor="secondary"
          value={activeTab}
          variant="scrollable"
        >
          {tabs.map(({ label, path }) => (
            <Tab key={path} label={label} value={path} />
          ))}
        </Tabs>
      </Box>

      <Outlet />
    </Container>
  );
};
